import { supabase } from "@/integrations/supabase/client";
import { extractEdgeFunctionError } from "@/lib/edgeFunctionError";
import { withTimeout } from "@/lib/withTimeout";

export type WebPushState =
  | "unsupported"
  | "unconfigured"
  | "denied"
  | "disabled"
  | "enabled";

function vapidKey() {
  return import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim() ?? "";
}

/** Clave VAPID en base64url → bytes para `applicationServerKey`. */
export function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

export function webPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Cuánto se espera como mucho a que el service worker quede activo. Un worker
 * ya instalado activa en milisegundos; si a los 5 s no lo ha hecho, está roto.
 */
const SERVICE_WORKER_READY_TIMEOUT_MS = 5000;

/**
 * Registro del service worker ya activo, o `null` si no hay ninguno.
 *
 * POR QUÉ NO SE USA `navigator.serviceWorker.ready` A SECAS
 * Esa promesa NUNCA se rechaza: si no hay ningún worker registrado se queda
 * PENDIENTE PARA SIEMPRE. Quien la espere se cuelga en silencio, y un
 * `.catch()` no lo salva porque no hay rechazo que atrapar. Ocurre en
 * desarrollo (el plugin PWA no registra el worker con `vite dev`) y también en
 * producción si el usuario lo desregistra o el navegador lo desaloja.
 *
 * Dos defensas: `getRegistration()` —que sí resuelve, con `undefined` cuando no
 * hay nada— como guarda, y un plazo por si el registro existe pero se queda
 * atascado instalando.
 */
async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!webPushSupported()) return null;

  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) return null;

  try {
    return await withTimeout(
      navigator.serviceWorker.ready,
      SERVICE_WORKER_READY_TIMEOUT_MS,
      "la activación del service worker",
    );
  } catch {
    // Worker registrado pero atascado: se trata como "no hay push disponible"
    // en vez de dejar colgado a quien llamó.
    return null;
  }
}

async function activeSubscription() {
  const registration = await readyRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

interface SubscriptionKeys {
  endpoint: string;
  p256dh: string;
  auth: string;
}

async function upsertSubscription({ endpoint, p256dh, auth }: SubscriptionKeys) {
  const { error } = await supabase.rpc("upsert_web_push_subscription", {
    p_endpoint: endpoint,
    p_p256dh: p256dh,
    p_auth: auth,
    p_user_agent: navigator.userAgent,
  });
  if (error) throw error;
}

async function persistSubscription(subscription: PushSubscription) {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error("El navegador no devolvió una suscripción push válida");
  }
  await upsertSubscription({
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });
}

export async function getWebPushState(): Promise<WebPushState> {
  if (!webPushSupported()) return "unsupported";
  if (!vapidKey()) return "unconfigured";
  if (Notification.permission === "denied") return "denied";
  return (await activeSubscription()) ? "enabled" : "disabled";
}

/**
 * Borra en Supabase la fila de una suscripción que ya no sirve y la cancela en
 * el navegador. Se usa cuando la persona quitó el permiso desde los ajustes del
 * navegador: sin esto el servidor seguiría enviando a un endpoint muerto hasta
 * recibir un 410, y la interfaz mostraría "encendido" cuando en realidad no
 * llega nada.
 */
async function dropSubscription(subscription: PushSubscription) {
  // Si la sesión ya no existe el RPC falla; la baja local se hace igualmente.
  try {
    await supabase.rpc("remove_web_push_subscription", {
      p_endpoint: subscription.endpoint,
    });
  } catch {
    /* sin sesión o sin red: la fila se desactivará al primer 410 del servidor */
  }
  try {
    await subscription.unsubscribe();
  } catch {
    /* el navegador ya la había cancelado */
  }
}

/**
 * Renueva en Supabase la suscripción que el navegador ya tiene y, de paso,
 * concilia los dos casos en los que la fila de la base queda obsoleta:
 * permiso revocado desde el navegador y clave VAPID cambiada en el servidor.
 */
export async function syncWebPushSubscription() {
  if (!webPushSupported() || !vapidKey()) return false;
  const subscription = await activeSubscription();
  if (!subscription) return false;

  if (Notification.permission !== "granted") {
    await dropSubscription(subscription);
    return false;
  }

  await persistSubscription(subscription);
  return true;
}

/**
 * Sube la suscripción que el service worker acaba de recrear tras un
 * `pushsubscriptionchange` (el worker no puede llamar al RPC: no tiene el JWT).
 */
export async function persistRotatedSubscription(input: Partial<SubscriptionKeys>) {
  if (!input.endpoint || !input.p256dh || !input.auth) return false;
  await upsertSubscription({
    endpoint: input.endpoint,
    p256dh: input.p256dh,
    auth: input.auth,
  });
  return true;
}

export async function enableWebPush() {
  if (!webPushSupported()) {
    throw new Error("Este navegador no admite notificaciones web");
  }
  const publicKey = vapidKey();
  if (!publicKey) {
    throw new Error("Las notificaciones aún no están configuradas en este entorno");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Las notificaciones están bloqueadas en el navegador"
        : "No se concedió permiso para enviar notificaciones",
    );
  }

  // Mismo cuelgue que en `readyRegistration`: sin worker activo, esperar a
  // `ready` dejaría el botón de activar avisos girando para siempre. Aquí sí es
  // un error que el usuario debe ver, porque pidió activar algo que no se puede.
  const registration = await readyRegistration();
  if (!registration) {
    throw new Error(
      "No hay un service worker activo en este navegador, así que los avisos no se pueden activar. Recarga la página e inténtalo de nuevo.",
    );
  }

  let subscription = await registration.pushManager.getSubscription();
  let created = false;
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    created = true;
  }

  try {
    await persistSubscription(subscription);
  } catch (error) {
    if (created) await subscription.unsubscribe();
    throw error;
  }
}

export async function disableWebPush() {
  const subscription = await activeSubscription();
  if (!subscription) return;

  const { error } = await supabase.rpc("remove_web_push_subscription", {
    p_endpoint: subscription.endpoint,
  });
  if (error) throw error;
  await subscription.unsubscribe();
}

export async function sendTestWebPush() {
  const { data, error } = await supabase.functions.invoke("send-test-notification", {
    body: {},
  });
  if (error) {
    throw new Error(
      await extractEdgeFunctionError(error, "No se pudo enviar la notificación de prueba"),
    );
  }
  return data as { sent: number; failed: number; deactivated: number };
}

/** Corta la entrega local cuando la sesión expiró y ya no existe JWT para RPC. */
export async function unsubscribeLocalWebPush() {
  const subscription = await activeSubscription();
  if (subscription) await subscription.unsubscribe();
}
